import { z } from 'zod';
import { MoneyCentsSchema, PositiveMoneyCentsSchema, } from '../types/sale/v4/execution';
import { BillingIntervalSchema } from '../types/subscriptions/enums';
import { MVP1_SUPPORTED_INTERVAL_COUNT, MVP1_SUPPORTED_INTERVALS, } from './enums';
import { ProviderExecutionScopeSchema } from './execution-scope';
import { BillingPolicySchema, BillingTimeZoneSchema, BillingSchedulePreviewSchema, AcceptedBillingScheduleSchema, billingPolicyMatchesInterval, } from './billing-schedule';
import { IsoTimestampSchema, ProviderAccountDbIdSchema, PublishedRevisionSchema, StripeAccountIdSchema, StripePriceIdSchema, StripeProductIdSchema, } from './primitives';
/**
 * Public Experiences — money, recurrence, the offer snapshot and consent.
 *
 * Money is ALWAYS an integer number of minor units (cents). There is no
 * floating-point dollar field anywhere in this contract, including in display
 * DTOs: formatting happens in the view layer via `formatMoneyCents`.
 */
/**
 * MVP1 is USD-only, expressed as a literal so a non-USD offer cannot be stored
 * or published by accident.
 *
 * Widening later: replace with `z.enum(['USD', 'CAD', ...])` (or a shared
 * currency enum). That is a backwards-compatible widening for readers and a
 * breaking change for writers, so it needs a CONTRACT_CHANGELOG entry and a
 * review of the minor-unit exponent assumption (all currently planned
 * currencies are 2-decimal).
 */
export const PublicExperienceCurrencySchema = z.literal('USD');
/**
 * Recurrence. `interval` reuses the existing `BillingIntervalEnum`
 * (DAILY/WEEKLY/MONTHLY/YEARLY) so persisted documents keep parsing if the
 * supported set widens; MVP1 input is narrowed at the admin request boundary by
 * `MVP1_SUPPORTED_INTERVALS` / `MVP1_SUPPORTED_INTERVAL_COUNT`.
 */
export const RecurrenceSchema = z
    .object({
    interval: BillingIntervalSchema,
    intervalCount: z.number().int().min(1).max(52).default(1),
})
    .strict();
/** True when the recurrence is one the MVP1 admin API accepts. */
export const isMvp1SupportedRecurrence = (recurrence) => MVP1_SUPPORTED_INTERVALS.includes(recurrence.interval) && recurrence.intervalCount === MVP1_SUPPORTED_INTERVAL_COUNT;
/**
 * Consent text version. Bumped whenever the wording changes; the accepted
 * version is stored with each submission as acceptance evidence, so historical
 * submissions keep pointing at the exact wording the customer saw.
 */
export const PXE_CONSENT_TEXT_VERSION_V1 = 'pxe-consent-v1';
export const ConsentTextVersionSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9._-]*$/, 'must be a lowercase consent version id');
/**
 * Placeholders the stored consent/cancellation wording may contain. They are
 * substituted at render time from published data, so the business name and the
 * amount a customer sees always come from the authoritative published offer and
 * never from client input.
 */
export const CONSENT_TEXT_PLACEHOLDERS = {
    businessName: '[BUSINESS NAME]',
    amount: '$___',
};
/** Owner-supplied MVP1 consent wording (PLAN.md §12 item 1). */
export const DEFAULT_CONSENT_TEXT_V1 = 'By submitting, you authorize [BUSINESS NAME] to charge $___ every month to the payment method provided until the recurring payment is cancelled.';
/** Owner-supplied MVP1 cancellation wording (PLAN.md §12 item 1). */
export const DEFAULT_CANCELLATION_NOTICE_V1 = 'To cancel future recurring payments, contact [BUSINESS NAME]. Cancellation stops future charges and does not automatically refund payments already processed.';
/** Consent body stored on the experience. Length-capped for the 8 KB bodyLimit. */
export const ConsentTextSchema = z.string().trim().min(20).max(2000);
/**
 * Display-only formatter for integer minor units. Never used for arithmetic and
 * never persisted; the contract stores cents only.
 */
export const formatMoneyCents = (amountCents, currency = 'USD') => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
}).format(amountCents / 100);
/**
 * Renders stored wording for display by substituting the placeholders with
 * published values. Pure and deterministic so the public page, the admin
 * preview and the fixtures all produce identical text.
 */
export const renderConsentText = (args) => args.template
    .split(CONSENT_TEXT_PLACEHOLDERS.businessName)
    .join(args.businessName)
    .split(CONSENT_TEXT_PLACEHOLDERS.amount)
    .join(formatMoneyCents(args.amountCents, args.currency ?? 'USD'));
/**
 * The editable part of an offer — everything an admin controls. No provider
 * identifiers and no fee fields: those are derived by the server at publish time.
 */
export const OfferDraftSchema = z
    .object({
    billingPolicy: BillingPolicySchema.optional(),
    amountCents: PositiveMoneyCentsSchema,
    currency: PublicExperienceCurrencySchema,
    interval: BillingIntervalSchema,
    intervalCount: z.number().int().min(1).max(52),
    consentText: ConsentTextSchema,
    consentTextVersion: ConsentTextVersionSchema,
    /**
     * Wording shown to the customer and in the admin UI explaining how to
     * cancel. Display copy only; cancellation itself is an admin action.
     */
    cancellationNotice: z.string().trim().min(20).max(2000),
})
    .strict();
/**
 * Server-derived Stripe objects backing a published offer — contract 1.0.0
 * shape, retained verbatim for `schemaVersion: 1` (platform-owned, destination
 * charge) experience documents.
 *
 * `accountId` is a **snapshot** of the connected account at publish time so a
 * later relink cannot silently redirect funds or break lookups;
 * `providerAccountDbId` is the `All_Payment_Accounts` row id (a UUID string —
 * those tables use Sequelize UUID primary keys).
 */
export const PublishedOfferProviderV1Schema = z
    .object({
    productId: StripeProductIdSchema,
    priceId: StripePriceIdSchema,
    accountId: StripeAccountIdSchema,
    providerAccountDbId: ProviderAccountDbIdSchema,
})
    .strict();
/**
 * Contract 2.0.0 provider linkage: the v1 fields (kept with their v1 meaning —
 * `accountId` is still the publish-time account snapshot) plus the REQUIRED
 * `executionScope`, which says which account owns the Product/Price and under
 * which charge model. v2 readers use `executionScope` only.
 */
export const PublishedOfferProviderSchema = PublishedOfferProviderV1Schema.extend({
    executionScope: ProviderExecutionScopeSchema,
}).strict();
/** Reader-side union: whichever linkage a stored published block carries. */
export const AnyPublishedOfferProviderSchema = z.union([
    PublishedOfferProviderSchema,
    PublishedOfferProviderV1Schema,
]);
/**
 * `applicationFeePercent` is computed at publish time from the business's
 * existing `financialDetails.onlineProcessingConfig.rate` applied to the fixed
 * amount (Stripe subscriptions accept only a percent), rounded to 2 dp.
 * `expectedPlatformFeeCents` is the integer fee that percent should produce for
 * this amount; each paid invoice is reconciled against it and a mismatch is
 * logged, never silently re-priced. The merchant absorbs the fee —
 * customer-pays splits are not supported. Under direct charges the same percent
 * becomes `subscription_data.application_fee_percent` (DEC-P2-10: pricing
 * unchanged).
 */
const PublishedOfferFeeShape = {
    applicationFeePercent: z.number().min(0).max(100),
    expectedPlatformFeeCents: MoneyCentsSchema,
};
const refinePublishedOfferFee = (offer) => offer.expectedPlatformFeeCents <= offer.amountCents;
const PUBLISHED_OFFER_FEE_REFINEMENT = {
    message: 'expectedPlatformFeeCents cannot exceed amountCents',
    path: ['expectedPlatformFeeCents'],
};
/**
 * A published offer as frozen by contract 1.0.0: the draft fields frozen at
 * publish time plus the server-computed fee representation and the v1 provider
 * objects. Retained verbatim for `schemaVersion: 1` experience documents.
 */
export const PublishedOfferV1Schema = OfferDraftSchema.extend({
    ...PublishedOfferFeeShape,
    provider: PublishedOfferProviderV1Schema,
})
    .strict()
    .refine(refinePublishedOfferFee, PUBLISHED_OFFER_FEE_REFINEMENT);
/**
 * A published offer under contract 2.0.0: identical to v1 except that the
 * provider linkage carries the required `executionScope`.
 */
export const PublishedOfferSchema = OfferDraftSchema.extend({
    billingTimeZone: BillingTimeZoneSchema.optional(),
    ...PublishedOfferFeeShape,
    provider: PublishedOfferProviderSchema,
})
    .strict()
    .refine(refinePublishedOfferFee, PUBLISHED_OFFER_FEE_REFINEMENT)
    .refine((value) => !value.billingPolicy ||
    (!!value.billingTimeZone &&
        billingPolicyMatchesInterval(value.billingPolicy, value.interval, value.intervalCount)), {
    path: ['billingPolicy'],
    message: 'Scheduled offers require a timezone and matching recurrence',
});
/**
 * Immutable snapshot of what the customer actually accepted, stored on the
 * submission. It deliberately omits provider identifiers and fee policy: it is
 * evidence of the customer's agreement, not a billing instruction.
 */
export const AcceptedOfferSnapshotSchema = z
    .object({
    billingSchedule: AcceptedBillingScheduleSchema.optional(),
    offerRevision: PublishedRevisionSchema,
    amountCents: PositiveMoneyCentsSchema,
    currency: PublicExperienceCurrencySchema,
    interval: BillingIntervalSchema,
    intervalCount: z.number().int().min(1).max(52),
    consentTextVersion: ConsentTextVersionSchema,
    /** The exact rendered sentence the customer saw, placeholders resolved. */
    consentTextRendered: z.string().min(1).max(2000),
})
    .strict()
    .refine((value) => !value.billingSchedule ||
    (value.billingSchedule.preview.amountCents === value.amountCents &&
        value.billingSchedule.preview.currency === value.currency &&
        value.billingSchedule.preview.interval === value.interval &&
        value.billingSchedule.preview.intervalCount === value.intervalCount), {
    path: ['billingSchedule'],
    message: 'Accepted schedule must match the offer money and recurrence',
});
/**
 * The authoritative offer as shown to a guest. Amounts come from the server
 * only; the guest never sends them back. Contains no provider identifiers, no
 * fee fields and no PII.
 *
 * The amount the customer is charged and the amount displayed are the SAME
 * value: `amountCents`. The merchant absorbs the platform fee (computed from
 * `financialDetails.onlineProcessingConfig`), and there are no customer splits,
 * surcharges or added fee lines for these online payments — so there is
 * deliberately no `displayTotalCents`, no fee line-item array and no
 * `customerSplits` anywhere in this contract.
 */
export const PublicOfferViewSchema = z
    .object({
    billingPreview: BillingSchedulePreviewSchema.optional(),
    offerRevision: PublishedRevisionSchema,
    amountCents: PositiveMoneyCentsSchema,
    currency: PublicExperienceCurrencySchema,
    interval: BillingIntervalSchema,
    intervalCount: z.number().int().min(1).max(52),
    /** Placeholders already resolved from published business name + amount. */
    consentText: z.string().min(1).max(2000),
    consentTextVersion: ConsentTextVersionSchema,
    cancellationNotice: z.string().min(1).max(2000),
    offerUpdatedAt: IsoTimestampSchema,
})
    .strict();
/**
 * Customer acceptance evidence supplied with a checkout create. `accepted` is a
 * literal `true` so an unchecked box cannot be submitted, and `textVersion` must
 * match the version the server is currently publishing.
 */
export const ConsentAcceptanceSchema = z
    .object({
    accepted: z.literal(true),
    textVersion: ConsentTextVersionSchema,
})
    .strict();
